import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  SelectChangeEvent,
  Container,
  Snackbar,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Save as SaveIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { getUsers, updateUser, User } from '../../services/userService';

// Estilos personalizados para el contenedor del formulario
const FormContainer = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  gap: '24px',
  marginTop: '16px',
});

// Estilos para los botones de acción
const ActionButtons = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '16px',
  marginTop: '24px',
});

type UserProfile = 'root' | 'admin' | 'mkt' | 'usuario' | 'superadmin';

interface FormData {
  profile: UserProfile;
  enabled: boolean;
}

const EditUserPage: React.FC = () => {
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState<FormData>({
    profile: 'usuario',
    enabled: true,
  });

  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      if (!userId) {
        navigate('/users');
        return;
      }

      try {
        const users = await getUsers();
        const foundUser = users.find(u => u.id === userId);
        
        if (!foundUser) {
          setSnackbar({
            open: true,
            message: 'Usuario no encontrado',
            severity: 'error',
          });
          setTimeout(() => navigate('/users'), 2000);
          return;
        }

        setUser(foundUser);
        setFormData({
          profile: foundUser.profile as UserProfile,
          enabled: foundUser.enabled,
        });
      } catch (error) {
        console.error('Error al cargar usuario:', error);
        setSnackbar({
          open: true,
          message: 'Error al cargar los datos del usuario',
          severity: 'error',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [userId, navigate]);

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};
    
    if (!formData.profile) {
      newErrors.profile = 'El perfil es requerido';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSelectChange = (e: SelectChangeEvent<UserProfile>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    
    if (errors.profile) {
      setErrors({
        ...errors,
        profile: '',
      });
    }
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      enabled: e.target.checked,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm() || !userId) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      const updateData = {
        id: userId,
        profile: formData.profile,
        enabled: formData.enabled,
      };
      
      await updateUser(updateData);
      
      setSnackbar({
        open: true,
        message: 'Usuario actualizado exitosamente',
        severity: 'success',
      });
      
      // Redirigir a la lista de usuarios después de un breve retraso
      setTimeout(() => {
        navigate('/users');
      }, 1500);
      
    } catch (error) {
      console.error('Error al actualizar el usuario:', error);
      const errorMessage = error instanceof Error ? error.message : 'Ocurrió un error inesperado al actualizar el usuario';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const profileOptions = [
    { value: 'root', label: 'Root' },
    { value: 'superadmin', label: 'Super Administrador' },
    { value: 'admin', label: 'Administrador' },
    { value: 'mkt', label: 'Marketing' },
    { value: 'usuario', label: 'Usuario' },
  ];

  if (loading) {
    return (
      <Container maxWidth={false} disableGutters sx={{ maxWidth: '100%', px: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container maxWidth={false} disableGutters sx={{ maxWidth: '100%', px: 0 }}>
        <Box sx={{ p: { xs: 1, sm: 2 } }}>
          <Typography variant="h6" color="error">
            Usuario no encontrado
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} disableGutters sx={{ maxWidth: '100%', px: 0 }}>
      <Box sx={{ mb: 4, width: '100%', p: { xs: 1, sm: 2 } }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Volver
        </Button>
        
        <Typography variant="h4" component="h1" gutterBottom>
          Editar Usuario
        </Typography>
        
        <Paper sx={{ p: { xs: 2, sm: 3 }, width: '100%' }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Información del Usuario
            </Typography>
            <Typography variant="body1" color="textSecondary">
              <strong>Nombre:</strong> {user.first_name} {user.last_name}
            </Typography>
            <Typography variant="body1" color="textSecondary">
              <strong>Email:</strong> {user.email}
            </Typography>
            {user.dni && (
              <Typography variant="body1" color="textSecondary">
                <strong>DNI:</strong> {user.dni}
              </Typography>
            )}
            {user.mobile && (
              <Typography variant="body1" color="textSecondary">
                <strong>Teléfono:</strong> {user.mobile}
              </Typography>
            )}
          </Box>

          <form onSubmit={handleSubmit}>
            <FormContainer>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
                <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                  <FormControl margin="normal" error={!!errors.profile} sx={{ flex: 1 }}>
                    <InputLabel id="profile-label">Perfil</InputLabel>
                    <Select
                      labelId="profile-label"
                      name="profile"
                      value={formData.profile}
                      onChange={handleSelectChange}
                      label="Perfil"
                    >
                      {profileOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.profile && (
                      <FormHelperText>{errors.profile}</FormHelperText>
                    )}
                  </FormControl>
                </Box>

                <Box sx={{ mt: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.enabled}
                        onChange={handleSwitchChange}
                        color="primary"
                      />
                    }
                    label={formData.enabled ? 'Usuario Activo' : 'Usuario Inactivo'}
                  />
                </Box>
              </Box>
            </FormContainer>
            
            <ActionButtons>
              <Button
                variant="outlined"
                onClick={() => navigate(-1)}
                size="large"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                size="large"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Guardando...' : 'Guardar Cambios'}
              </Button>
            </ActionButtons>
          </form>
        </Paper>
      </Box>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default EditUserPage;
